import { useEffect, useState, useMemo } from "react";
import { api, formatApiError, downloadFile } from "@/lib/api";
import { useTranslation } from "react-i18next";
import i18n from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Sparkle, FloppyDisk, CheckCircle, FileText, DownloadSimple, PencilSimple, Warning } from "@phosphor-icons/react";

const PERIODS = ["T1", "T2", "T3"];

export default function Grades() {
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [students, setStudents] = useState([]);
  const [grades, setGrades] = useState([]);
  const [classId, setClassId] = useState("");
  const [subjectId, setSubjectId] = useState("");
  const [period, setPeriod] = useState("T1");
  const [editing, setEditing] = useState({});
  const [reportOpen, setReportOpen] = useState(false);
  const [reportStudent, setReportStudent] = useState(null);
  const [report, setReport] = useState(null);
  const [aiText, setAiText] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [editGrade, setEditGrade] = useState(null);   // {grade, student, is_published}
  const [editForm, setEditForm] = useState({ score: "", reason: "", confirm: false });

  useEffect(() => {
    api.get("/classes").then(r => setClasses(r.data)).catch(e => toast.error(formatApiError(e)));
  }, []);

  useEffect(() => {
    if (!classId) { setSubjects([]); setStudents([]); return; }
    Promise.all([
      api.get("/subjects", { params: { class_id: classId } }),
      api.get("/students", { params: { class_id: classId } }),
    ]).then(([sub, st]) => {
      setSubjects(sub.data);
      setStudents(st.data);
      if (sub.data.length && !subjectId) setSubjectId(sub.data[0].id);
    });
    // eslint-disable-next-line
  }, [classId]);

  useEffect(() => {
    if (!classId || !subjectId) { setGrades([]); return; }
    api.get("/grades", { params: { class_id: classId, subject_id: subjectId, period } })
      .then(r => setGrades(r.data)).catch(e => toast.error(formatApiError(e)));
  }, [classId, subjectId, period]);

  const gradeByStudent = useMemo(() => {
    const m = {};
    grades.forEach(g => { m[g.student_id] = g; });
    return m;
  }, [grades]);

  const setScore = (student_id, value) => {
    setEditing(prev => ({ ...prev, [student_id]: value }));
  };

  const saveAll = async () => {
    const entries = Object.entries(editing).filter(([_, v]) => v !== "" && v != null);
    if (!entries.length) { toast.info("Aucune modification"); return; }
    try {
      for (const [student_id, score] of entries) {
        await api.post("/grades", {
          student_id, subject_id: subjectId, class_id: classId, period,
          score: Number(score), max_score: 20, assessment_type: "composition",
        });
      }
      toast.success(`${entries.length} note(s) enregistrée(s)`);
      setEditing({});
      const r = await api.get("/grades", { params: { class_id: classId, subject_id: subjectId, period } });
      setGrades(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const publish = async () => {
    if (!window.confirm("Publier les notes de cette classe pour la période ? Les parents/élèves y auront accès.")) return;
    try {
      await api.post("/grades/publish", null, { params: { class_id: classId, period } });
      toast.success("Notes publiées");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openEditGrade = (student, g) => {
    setEditGrade({ grade: g, student, is_published: g.status === "published" });
    setEditForm({ score: g.score, reason: "", confirm: false });
  };
  const saveEditGrade = async () => {
    if (!editGrade) return;
    if (editGrade.is_published && (!editForm.reason || editForm.reason.trim().length < 5)) {
      toast.error("Motif obligatoire (≥5 caractères) pour une note publiée"); return;
    }
    if (editGrade.is_published && !editForm.confirm) {
      toast.error("Cochez la confirmation pour modifier une note publiée"); return;
    }
    try {
      const body = { score: Number(editForm.score) };
      if (editGrade.is_published) { body.reason = editForm.reason.trim(); body.confirm = true; }
      await api.patch(`/grades/${editGrade.grade.id}`, body);
      toast.success(editGrade.is_published ? "Note modifiée · audit critique loggé" : "Note modifiée");
      setEditGrade(null);
      const r = await api.get("/grades", { params: { class_id: classId, subject_id: subjectId, period } });
      setGrades(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openReport = async (st) => {
    setReportStudent(st);
    setReportOpen(true);
    setAiText("");
    try {
      const { data } = await api.get(`/grades/report/${st.id}`, { params: { period } });
      setReport(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const generateAI = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/report-comment", {
        student_id: reportStudent.id, class_id: classId, period,
      });
      setAiText(data.comment);
      toast.success("Appréciation IA générée");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAiLoading(false); }
  };

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto">
      <header className="mb-6">
        <div className="text-xs uppercase tracking-widest text-secondary font-semibold">Évaluation</div>
        <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight">Notes & Bulletins</h1>
      </header>

      <Card className="p-4 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <div>
          <Label>Classe</Label>
          <Select value={classId} onValueChange={setClassId}>
            <SelectTrigger data-testid="select-grade-class"><SelectValue placeholder="Choisir…" /></SelectTrigger>
            <SelectContent>{classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Matière</Label>
          <Select value={subjectId} onValueChange={setSubjectId} disabled={!subjects.length}>
            <SelectTrigger data-testid="select-grade-subject"><SelectValue placeholder="Choisir…" /></SelectTrigger>
            <SelectContent>{subjects.map(s => <SelectItem key={s.id} value={s.id}>{s.name} (coef. {s.coefficient})</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Période</Label>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger data-testid="select-grade-period"><SelectValue /></SelectTrigger>
            <SelectContent>{PERIODS.map(p => <SelectItem key={p} value={p}>{p === "T1" ? "Trimestre 1" : p === "T2" ? "Trimestre 2" : "Trimestre 3"}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex items-end gap-2">
          <Button onClick={saveAll} disabled={!subjectId} className="flex-1 rounded-full bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="btn-save-grades">
            <FloppyDisk size={16} className="mr-2" /> Enregistrer
          </Button>
          <Button onClick={publish} variant="outline" disabled={!classId} className="rounded-full" data-testid="btn-publish-grades">
            <CheckCircle size={16} className="mr-2" /> Publier
          </Button>
        </div>
      </Card>

      {classId && subjectId ? (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-3">Élève</th>
              <th className="px-4 py-3">Matricule</th>
              <th className="px-4 py-3">Note actuelle</th>
              <th className="px-4 py-3">Nouvelle note (/20)</th>
              <th className="px-4 py-3 text-right">Bulletin</th>
            </tr></thead>
            <tbody>
              {students.map((s, i) => {
                const g = gradeByStudent[s.id];
                return (
                  <tr key={s.id} className={`border-b border-border last:border-0 ${i % 2 ? "bg-muted/30" : ""}`}>
                    <td className="px-4 py-3 font-medium">{s.first_name} {s.last_name}</td>
                    <td className="px-4 py-3 font-mono text-xs">{s.matricule}</td>
                    <td className="px-4 py-3 tabular-nums">
                      {g ? (
                        <span className="inline-flex items-center gap-2">
                          <span>{g.score}/{g.max_score}</span>
                          {g.status === "published" && <span className="text-[9px] uppercase tracking-wider bg-secondary/15 text-secondary px-1.5 py-0.5 rounded font-mono">publiée</span>}
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => openEditGrade(s, g)} data-testid={`btn-edit-grade-${s.id}`}>
                            <PencilSimple size={12} className="text-primary" />
                          </Button>
                        </span>
                      ) : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <Input
                        type="number" step="0.25" min="0" max="20"
                        value={editing[s.id] ?? ""} onChange={e => setScore(s.id, e.target.value)}
                        className="w-24 tabular-nums" data-testid={`input-score-${s.id}`}
                      />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button variant="outline" size="sm" onClick={() => openReport(s)} data-testid={`btn-bulletin-${s.id}`}>
                        <FileText size={14} className="mr-1" /> Voir
                      </Button>
                    </td>
                  </tr>
                );
              })}
              {!students.length && <tr><td colSpan={5} className="px-4 py-12 text-center text-sm text-muted-foreground">Aucun élève dans cette classe.</td></tr>}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center text-sm text-muted-foreground py-12">Sélectionnez une classe et une matière.</div>
      )}

      <Dialog open={!!editGrade} onOpenChange={v => !v && setEditGrade(null)}>
        <DialogContent data-testid="dialog-edit-grade">
          {editGrade && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {editGrade.is_published && <Warning size={20} className="text-destructive" weight="duotone" />}
                  Modifier la note — {editGrade.student.first_name} {editGrade.student.last_name}
                </DialogTitle>
              </DialogHeader>
              {editGrade.is_published && (
                <div className="text-xs bg-destructive/10 border border-destructive/30 text-destructive rounded-md p-3">
                  <b>Attention :</b> cette note est déjà publiée. Sa modification déclenchera un audit <b>critique</b> et sera notifiée aux parents concernés. Un motif écrit est obligatoire.
                </div>
              )}
              <div>
                <Label>Note ancienne</Label>
                <Input disabled value={`${editGrade.grade.score}/${editGrade.grade.max_score}`} />
              </div>
              <div>
                <Label>Nouvelle note</Label>
                <Input type="number" step="0.25" min="0" max={editGrade.grade.max_score}
                       value={editForm.score} onChange={e => setEditForm({ ...editForm, score: e.target.value })}
                       data-testid="input-edit-score" />
              </div>
              {editGrade.is_published && (
                <>
                  <div>
                    <Label>Motif de la modification (obligatoire, ≥5 caractères)</Label>
                    <Textarea rows={3} value={editForm.reason}
                              onChange={e => setEditForm({ ...editForm, reason: e.target.value })}
                              placeholder="Ex : erreur de saisie détectée après double correction"
                              data-testid="input-edit-reason" />
                  </div>
                  <label className="flex items-start gap-2 text-sm cursor-pointer bg-destructive/5 border border-destructive/20 rounded-md p-2">
                    <input type="checkbox" checked={editForm.confirm} className="mt-1"
                           onChange={e => setEditForm({ ...editForm, confirm: e.target.checked })}
                           data-testid="chk-edit-confirm" />
                    <span>Je confirme vouloir modifier cette note publiée. Cette action est traçable et irréversible.</span>
                  </label>
                </>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditGrade(null)}>Annuler</Button>
                <Button onClick={saveEditGrade} data-testid="btn-save-edit-grade"
                        disabled={editGrade.is_published && (!editForm.confirm || (editForm.reason || "").trim().length < 5)}>
                  Enregistrer
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="max-w-3xl" data-testid="dialog-bulletin">
          <DialogHeader><DialogTitle>Bulletin — {reportStudent?.first_name} {reportStudent?.last_name} · {period}</DialogTitle></DialogHeader>
          {report ? (
            <>
              <div className="border border-border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead><tr className="bg-muted/50 border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="px-3 py-2">Matière</th><th className="px-3 py-2">Coef.</th><th className="px-3 py-2">Moyenne /20</th><th className="px-3 py-2">Pondérée</th>
                  </tr></thead>
                  <tbody>
                    {report.lines.map((l, i) => (
                      <tr key={i} className="border-b border-border last:border-0">
                        <td className="px-3 py-2">{l.subject}</td>
                        <td className="px-3 py-2 tabular-nums">{l.coefficient}</td>
                        <td className="px-3 py-2 tabular-nums">{l.average}</td>
                        <td className="px-3 py-2 tabular-nums font-semibold">{l.weighted}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-3">
                <div className="bg-primary/5 border border-primary/20 rounded-md p-4">
                  <div className="text-xs uppercase tracking-widest text-primary font-semibold">Moyenne générale</div>
                  <div className="font-heading text-3xl font-bold tabular-nums mt-1">{report.average}/20</div>
                </div>
                <div className="bg-accent/5 border border-accent/20 rounded-md p-4">
                  <div className="text-xs uppercase tracking-widest text-accent font-semibold">Mention</div>
                  <div className="font-heading text-3xl font-bold mt-1">{report.mention}</div>
                </div>
              </div>
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <Label>Appréciation</Label>
                  <Button size="sm" variant="outline" onClick={generateAI} disabled={aiLoading} data-testid="btn-ai-comment">
                    <Sparkle size={14} className="mr-1" weight="fill" /> {aiLoading ? "Rédaction…" : "Générer par IA"}
                  </Button>
                </div>
                <Textarea value={aiText} onChange={e => setAiText(e.target.value)} rows={4} placeholder="Cliquez sur Générer par IA ou saisissez manuellement…" data-testid="textarea-comment" />
              </div>
            </>
          ) : <div className="text-sm text-muted-foreground">Chargement…</div>}
          <DialogFooter className="flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => downloadFile(`/bulletins/${reportStudent.id}/pdf?period=${period}&lang=fr`, `bulletin_${reportStudent.matricule}_${period}_fr.pdf`).catch(e => toast.error(e.message))}
              data-testid="btn-download-bulletin-fr"
            >
              <DownloadSimple size={14} className="mr-1" /> Bulletin FR
            </Button>
            <Button
              variant="outline"
              onClick={() => downloadFile(`/bulletins/${reportStudent.id}/pdf?period=${period}&lang=en`, `report_${reportStudent.matricule}_${period}_en.pdf`).catch(e => toast.error(e.message))}
              data-testid="btn-download-bulletin-en"
            >
              <DownloadSimple size={14} className="mr-1" /> Report EN
            </Button>
            <Button
              variant="outline"
              onClick={() => downloadFile(`/bulletins/${reportStudent.id}/pdf-annual?lang=fr`, `bulletin_annuel_${reportStudent.matricule}_fr.pdf`).catch(e => toast.error(e.message))}
              className="border-primary/40 text-primary hover:bg-primary/5"
              data-testid="btn-download-bulletin-annual-fr"
            >
              <DownloadSimple size={14} className="mr-1" /> Bulletin annuel FR
            </Button>
            <Button
              variant="outline"
              onClick={() => downloadFile(`/bulletins/${reportStudent.id}/pdf-annual?lang=en`, `annual_report_${reportStudent.matricule}_en.pdf`).catch(e => toast.error(e.message))}
              className="border-primary/40 text-primary hover:bg-primary/5"
              data-testid="btn-download-bulletin-annual-en"
            >
              <DownloadSimple size={14} className="mr-1" /> Annual EN
            </Button>
            <Button
              variant="outline"
              onClick={async () => {
                if (!confirm(`Envoyer le bulletin annuel de ${reportStudent.first_name} aux parents par email ?`)) return;
                try {
                  const r = await api.post(`/bulletins/${reportStudent.id}/email-annual?lang=${i18n.language.startsWith("en") ? "en" : "fr"}`);
                  toast.success(`Bulletin envoyé (${r.data.emails_sent}/${r.data.attempted} email${r.data.attempted > 1 ? "s" : ""}).`);
                } catch (e) { toast.error(formatApiError(e)); }
              }}
              className="border-accent/50 text-accent hover:bg-accent/10"
              data-testid="btn-email-bulletin-annual"
            >
              📧 Envoyer aux parents
            </Button>
            <Button onClick={() => setReportOpen(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
